import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { getErpApiBase, resolveCompanyAssetUrl } from '../lib/companyAssets';

const CompanyBrandingContext = createContext(null);

const CACHE_KEY = 'site_company_branding';

function readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') return parsed;
  } catch {
    /* ignore */
  }
  return null;
}

function writeCache(data) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(data));
  } catch {
    /* ignore */
  }
}

export function CompanyBrandingProvider({ children }) {
  const cached = readCache();
  const [name, setName] = useState(cached?.name || '');
  const [logoUrl, setLogoUrl] = useState(cached?.logo_url || '');
  const [website, setWebsite] = useState(cached?.website || '');
  const [loading, setLoading] = useState(!cached);

  const refresh = useCallback(async () => {
    const apiBase = getErpApiBase() || '/api';
    try {
      const response = await fetch(`${apiBase}/company/branding`, {
        method: 'GET',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const payload = await response.json();
      const data = payload?.data || payload || {};
      const next = {
        name: data.name || '',
        logo_url: data.logo_url || '',
        website: data.website || '',
      };
      setName(next.name);
      setLogoUrl(next.logo_url);
      setWebsite(next.website);
      writeCache(next);
    } catch (err) {
      console.warn('Company branding unavailable:', err?.message || err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const logoSrc = useMemo(() => resolveCompanyAssetUrl(logoUrl), [logoUrl]);

  const value = useMemo(
    () => ({
      name,
      logoUrl,
      logoSrc,
      website,
      loading,
      refresh,
      displayName: name || null,
    }),
    [name, logoUrl, logoSrc, website, loading, refresh]
  );

  return (
    <CompanyBrandingContext.Provider value={value}>
      {children}
    </CompanyBrandingContext.Provider>
  );
}

export function useCompanyBranding() {
  const ctx = useContext(CompanyBrandingContext);
  if (!ctx) {
    throw new Error('useCompanyBranding must be used within CompanyBrandingProvider');
  }
  return ctx;
}
